import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { PURGE } from 'redux-persist';

export interface StaticMapState {
  componentId: string;
  initialFocusLocation: string;
  currentFocusLocation?: string;
  isInGlobalFocusMode: boolean;
  initialZoomLevel: number;
  currentZoomLevel?: number;
}
const initialState = {
  mapStates: [] as StaticMapState[]
};

const googleStaticMapSlice = createSlice({
  name: 'googleStaticMap',
  initialState,
  reducers: {
    setInitialMapState: (
      state,
      { payload: { componentId, initialFocusLocation, initialZoomLevel } }
    ) => {
      const mapState = state.mapStates.find(
        (x) => x.componentId === componentId
      );
      if (!mapState) {
        state.mapStates.push({
          componentId,
          initialFocusLocation,
          currentFocusLocation: initialFocusLocation,
          isInGlobalFocusMode: true,
          initialZoomLevel,
          currentZoomLevel: initialZoomLevel
        });
      } else {
        mapState.isInGlobalFocusMode = true;
        mapState.initialZoomLevel = initialZoomLevel;
        mapState.currentZoomLevel = initialZoomLevel;
        mapState.currentFocusLocation = initialFocusLocation;
        mapState.initialFocusLocation = initialFocusLocation;
      }
    },
    focusLocation: (state, { payload: { componentId, focusLocation } }) => {
      const mapState = state.mapStates.find(
        (x) => x.componentId === componentId
      );
      if (mapState) {
        mapState.isInGlobalFocusMode = false;
        mapState.currentFocusLocation = focusLocation;
        mapState.currentZoomLevel = mapState.initialZoomLevel;
      }
    },
    zoomIn: (state, { payload }) => {
      const mapState = state.mapStates.find((x) => x.componentId === payload);
      if (mapState) {
        mapState.isInGlobalFocusMode = false;
        mapState.currentFocusLocation =
          mapState.currentFocusLocation ?? mapState.initialFocusLocation;
        if (!mapState.currentZoomLevel)
          mapState.currentZoomLevel = mapState.initialZoomLevel;
        mapState.currentZoomLevel++;
      }
    },
    zoomOut: (state, { payload }) => {
      const mapState = state.mapStates.find((x) => x.componentId === payload);
      if (mapState) {
        mapState.isInGlobalFocusMode = false;
        mapState.currentFocusLocation =
          mapState.currentFocusLocation ?? mapState.initialFocusLocation;
        if (!mapState.currentZoomLevel)
          mapState.currentZoomLevel = mapState.initialZoomLevel;
        mapState.currentZoomLevel--;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  }
});

const selectSelf = (state: RootState) => state.googleStaticMap;
const selectMapStatesByComponentId = (state: RootState, componentId: string) =>
  componentId;

export const selectMapStateByComponentId = createSelector(
  selectSelf,
  selectMapStatesByComponentId,
  (googleStaticMap, componentId) => {
    const { isInGlobalFocusMode, currentFocusLocation, currentZoomLevel } =
      googleStaticMap.mapStates.find(
        (mapState) => mapState.componentId === componentId
      ) ?? {};
    return {
      isInGlobalFocusMode,
      currentFocusLocation,
      currentZoomLevel
    };
  }
);

export const { zoomIn, zoomOut, setInitialMapState, focusLocation } =
  googleStaticMapSlice.actions;

export default googleStaticMapSlice.reducer;
